<template>
  <div
    ref="containerRef"
    class="max-h-[inherit] overflow-y-auto"
  >
    <ul
      v-if="reviews?.length"
      class="space-y-8"
    >
      <li
        v-for="(review, index) in reviews"
        :key="review.Id ?? `review-${index}`"
        :ref="el => listRefs[index] = (el as HTMLElement)"
        :class="{ 'animate-pulse-primary': scrollToIndex === index }"
      >
        <TReview
          :review="review"
          avatar-size="md"
          :max-lines="maxLines"
          @report="handleReportReview(review.Id)"
        />
      </li>

      <li
        ref="bottomEl"
        class="text-center"
      >
        <AtomSpinner v-if="isLoading" />
      </li>
    </ul>
    <div v-else>
      <p>{{ t('no-reviews') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { GLOBAL_MODAL_COMPONENTS } from '~/constants/modals'

import type { RvReviewDisplay } from './types'

const props = defineProps<{
  reviews: RvReviewDisplay[]
  isLoading?: boolean
  scrollToIndex?: number
  maxLines?: number
}>()

const emit = defineEmits<{
  'end-of-scroll': []
}>()

const { t } = useI18n()

const containerRef = ref<HTMLElement>()
const listRefs = ref<HTMLElement[]>([])

function scrollTo(i?: number) {
  if (i === undefined) {
    return
  }

  const el = listRefs.value[i]

  if (el) {
    setTimeout(() => {
      containerRef.value?.scrollTo({
        top: el.offsetTop - 90,
        behavior: 'smooth',
      })
    }, 200) // animation purpose :)
  }
}

watch([() => listRefs.value.length, () => containerRef.value], ([length], [container]) => {
  if (!props.scrollToIndex || !container) return
  if (length > props.scrollToIndex) {
    scrollTo(props.scrollToIndex)
  }
})

const bottomEl = ref<HTMLElement>()
const isBottomVisible = useElementVisibility(bottomEl)

watch(isBottomVisible, (newVal, oldVal) => {
  if (newVal === oldVal || !newVal) return
  emit('end-of-scroll')
})

const { queueModal } = useModals()
function handleReportReview(reviewId?: Nullable<number>) {
  if (!reviewId) {
    return
  }

  queueModal({
    globalModal: {
      tag: GLOBAL_MODAL_COMPONENTS.BookingReviewsReportModal,
      data: {
        reviewId,
      },
    },
  })
}
</script>

<i18n lang="json">
{
  "en": {
    "no-reviews": "No reviews found."
  },
  "fr": {
    "no-reviews": "Aucun avis trouvé."
  }
}
</i18n>
